/**
 * ApprovalControls — approve / reject buttons for a queued trigger action.
 */
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

interface ApprovalControlsProps {
  pendingId: string;
  onDecided: () => void;
  onApprove: (id: string) => Promise<void>;
  onReject: (id: string) => Promise<void>;
  compact?: boolean;
}

export function ApprovalControls({
  pendingId,
  onDecided,
  onApprove,
  onReject,
  compact = false,
}: ApprovalControlsProps) {
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (kind: "approve" | "reject") => {
    setBusy(kind);
    setError(null);
    try {
      if (kind === "approve") await onApprove(pendingId);
      else await onReject(pendingId);
      onDecided();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Action failed";
      if (/not pending|already approved/i.test(msg)) {
        onDecided();
        return;
      }
      setError(msg);
    } finally {
      setBusy(null);
    }
  };

  const btn = compact
    ? "px-2 py-1 text-[11px]"
    : "px-2.5 py-1.5 text-xs";

  return (
    <div className="space-y-1.5" data-testid="approval-controls">
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void run("approve")}
          className={`inline-flex items-center gap-1.5 font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 ${btn}`}
        >
          <FontAwesomeIcon icon={faCheck} />
          {busy === "approve" ? "Approving…" : "Approve"}
        </button>
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void run("reject")}
          className={`inline-flex items-center gap-1.5 font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 disabled:opacity-50 ${btn}`}
        >
          <FontAwesomeIcon icon={faXmark} />
          {busy === "reject" ? "Rejecting…" : "Reject"}
        </button>
      </div>
    </div>
  );
}
